import { ALL_POSTS } from '../../utils/constants'
import { ROUTES } from '../../utils/constants/enum'
import {
    allPostsFragment,
    calloutBlockFragment,
    carouselBlockFragment,
    codeBlockFragment,
    contentGridFragment,
    imageConnectionFragment,
    imageGridFragment,
    pointerBlockFragment,
    promoBannerFragement,
    providerSeoFragment,
    seoFragment,
    splitImageBlockFragment,
    splitTextBlockFragment,
    testimonialFragment,
    textBlockFragment,
    textFragment,
    treatmentResultBlock,
} from './fragments'

export const SingleStaticPageQuery = (url: string) => `{
    all_static_page(where: {url: "${url}"}) {
        items {
          title
          url
          body_left {
            ... on StaticPageBodyLeftTextBlock {
              ${textBlockFragment}
            }
          }
          body_right {
            ... on StaticPageBodyRightTextBlock {
              ${textBlockFragment}
            }
          }
          ${seoFragment}
        }
      }
}`

export const PostsLandingPageQuery = (url: string, category: string) => `{
  all_dynamic_page(where: {url: "${url}"}) {
    items {
      title
      sub_head
      ${seoFragment}
      content {
        body {
          ... on BodyBodyListings {
            __typename
            listings {
              title
              listingsConnection {
                edges {
                  node {
                    ... on Post {
                      title
                      url
                      category
                      free_content
                      description
                      short_title
                      thumbnailConnection {
                        ${imageConnectionFragment}
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  all_post(where: {category: "${category}"}, skip: 0, limit: 12) {
    ${allPostsFragment}
  }
}`

export const SubCategoryPostsQuery = (
    subCategory: string,
    category: string
) => `{
  all_post(
    where: {${
        subCategory !== ALL_POSTS.title
            ? `sub_category: {sub_category: {title: "${subCategory}"}},`
            : ''
    } category: "${category}"}
    limit: 10
    skip: 0
  ) {
    ${allPostsFragment}
  }
}`

export const PostByUrlQuery = (url: string) => `{
  all_post(where: {url: "/${url}"}, limit: 1) {
    items {
      author
      categoriesConnection {
        edges {
          node {
            ... on Category {
              title
              iconConnection {
                ${imageConnectionFragment}
              }
            }
          }
        }
      }
      content {
        ... on PostContentText {
          __typename
          text {
            text
          }
        }
      }
      imageConnection {
        ${imageConnectionFragment}
      }
      recommended_treatmentsConnection {
        edges {
          node {
            ... on Treatment {
              title
              duration
              description
              short_description
              imageConnection {
                ${imageConnectionFragment}
              }
            }
          }
        }
      }
      short_description
      thumbnailConnection {
        ${imageConnectionFragment}
      }
      title
      url
      ${seoFragment}
    }
  }
}`

export const HomePageQuery = () => `{
  all_home {
    items {
      title
      ${seoFragment}
      hero {
        background_imageConnection {
          ${imageConnectionFragment}
        }
        title
        link {
          href
          title
        }
        intro_text
        intro_background_imageConnection {
          ${imageConnectionFragment}
        }
      }
      banner_logoConnection {
          ${imageConnectionFragment}
        }
      content {
        body {
          ... on BodyBodySponsors {
            __typename
            sponsors {
              sponsors {
                ... on BodyBodySponsorsBlockSponsorsLogo {
                  logo {
                    imageConnection {
                      ${imageConnectionFragment}
                    }
                    link {
                      href
                      title
                    }
                    text
                  }
                }
              }
              title
            }
          }
          ... on BodyBodyCallout {
            __typename
            callout {
              background_color
              background_color_2
              text_color
              image_layout
              description
              title
              link_imageConnection {
                ${imageConnectionFragment}
              }
              imageConnection {
                ${imageConnectionFragment}
              }
              link {
                href
                title
              }
            }
          }
          ... on BodyBodyListings {
            __typename
            listings {
              button_1 {
                href
                title
              }
              button_2 {
                href
                title
              }
              more_link_url {
                href
                title
              }
              title
              type
              listingsConnection {
                edges {
                  node {
                    ... on Post {
                      short_title
                      url
                      description
                      category
                      thumbnailConnection {
                        ${imageConnectionFragment}
                      }
                    }
                    ... on VideoCategories {
                      title
                      description
                      playlist_id
                      thumbnailConnection {
                        ${imageConnectionFragment}
                      }
                    }
                    ... on Creator {
                      title
                      url
                      description
                      category
                      playlist_id
                      thumbnailConnection {
                        ${imageConnectionFragment}
                      }
                    }
                    ... on Testimonial {
                      title
                      url
                      customer {
                        name
                        description
                        link {
                          href
                          title
                        }
                      }
                      thumbnailConnection {
                        ${imageConnectionFragment}
                      }
                    }

                  }
                }
              }
            }
          }
          ... on BodyBodyCodeBlock {
            __typename
            code_block {
              background_imageConnection {
                ${imageConnectionFragment}
              }
              text
              type
            }
          }
        }
      }
    }
  }
}`

export const DevicePageQuery = () => `{
  all_dynamic_page(where: {url: "${ROUTES.DEVICES}"}) {
    items {
      title
      url
      background_imageConnection {
        ${imageConnectionFragment}
      }
      mobile_background_imageConnection{
        ${imageConnectionFragment}
      }
      content {
        body {
          ... on BodyBodyCallout {
           ${calloutBlockFragment}
          }
          ... on BodyBodyPointersBlock {
          ${pointerBlockFragment}
          }
        }
      }
      ${seoFragment}
      ${promoBannerFragement}
    }
  }
}`

export const RewardsPageQuery = () => `{
  all_dynamic_page(where: {url: "${ROUTES.REWARDS}"}) {
    items {
      title
      ${seoFragment}
      ${promoBannerFragement}
      background_imageConnection {
        ${imageConnectionFragment}
      }
      mobile_background_imageConnection{
        ${imageConnectionFragment}
      }
      content {
        body {
          ... on BodyBodyCallout {
            ${calloutBlockFragment}
          }
          ... on BodyBodySplitImageBlock {
            ${splitImageBlockFragment}
          }
        }
      }
    }
  }
}`

export const LearnAboutSkinConcernPageQuery = () => `{
  all_dynamic_page(where: {url: "${ROUTES.SKIN_CONDITIONS}"}) {
    items {
      ${promoBannerFragement}
      content {
        body {
          ... on BodyBodyListing {
            __typename
            listing {
              title
              type
              listingsConnection {
                edges {
                  node {
                    ... on Post {
                      title
                      url
                      short_description
                      author
                      thumbnailConnection {
                        ${imageConnectionFragment}
                      }
                    }
                  }
                }
              }
            }
          }
          ... on BodyBodyCallout {
            ${calloutBlockFragment}
          }
        }
      }
      title
      description
      url
      ${seoFragment}
    }
  }
}`

export const StorePageQuery = () => `{
  all_dynamic_page(where: {url: "${ROUTES.STORE}"}) {
    items {
      title
      url
      background_imageConnection {
        ${imageConnectionFragment}
      }
      mobile_background_imageConnection{
        ${imageConnectionFragment}
      }
      ${seoFragment}
      ${promoBannerFragement}
      content {
        body {
          ... on BodyBodyListing {
            __typename
            listing {
              title
              type
              listingsConnection {
                edges {
                  node {
                    ... on ProductCategory {
                      title
                      sub_title
                      description
                      imageConnection {
                        ${imageConnectionFragment}
                      }
                      link {
                        href
                        title
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}`

export const PostsQuery = () => `{
  all_post(limit: 12, skip: 0) {
    items {
      author
      title
      url
      short_description
      thumbnailConnection {
        ${imageConnectionFragment}
      }
    }
  }
}`

export const TalkwithSalePageQuery = (source: string) => `{
  all_dynamic_page(where: {url: "${ROUTES.TALK_WITH_SALES}${source}/"}) {
    items {
      background_imageConnection {
        ${imageConnectionFragment}
      }
      mobile_background_imageConnection{
        ${imageConnectionFragment}
      }
      ${seoFragment}
      ${promoBannerFragement}
      title
      content {
        body {
        ... on BodyBodyImageGrid {
            ${imageGridFragment}
          }
            ... on BodyBodyText {
            ${textFragment}
          }
          ... on BodyBodyPointersBlock {
            ${pointerBlockFragment}
          }
        }
      }
    }
  }
}`
export const TreatmentTypePageQuery = () => `{
  all_dynamic_page(where: {url: "${ROUTES.TREATMENT_TYPES}"}) {
    items {
      title
      url
      background_imageConnection {
        ${imageConnectionFragment}
      }
      mobile_background_imageConnection{
        ${imageConnectionFragment}
      }
      ${seoFragment}
      ${promoBannerFragement}
      content {
        body {
          ... on BodyBodyListing {
            __typename
            listing {
              background_color
              imageConnection {
               ${imageConnectionFragment}
              }
              link_color
              sub_head
              title
              type
              description
              more_link {
                href
                title
              }
              listingsConnection {
                edges {
                  node {
                    ... on Treatment {
                      title
                      duration
                      url
                      description
                      short_description
                      imageConnection {
                        ${imageConnectionFragment}
                      }
                    }
                  }
                }
              }
            }
          }
          ... on BodyBodyCallout {
            ${calloutBlockFragment}
          }
        }
      }
    }
  }
}`

export const TreatmentsPageQuery = () => `{
  all_dynamic_page(where: {url: "${ROUTES.TREATMENTS}"}) {
    items {
      title
      url
      background_imageConnection {
        ${imageConnectionFragment}
      }
      mobile_background_imageConnection{
        ${imageConnectionFragment}
      }
      ${seoFragment}
      ${promoBannerFragement}
      content {
        body {
          ... on BodyBodySplitImageBlock {
            ${splitImageBlockFragment}
          }
          ... on BodyBodyCallout {
            ${calloutBlockFragment}
          }
          ... on BodyBodyCarousel {
            ${carouselBlockFragment}
          }

          ... on BodyBodyTreatmentResults {
            ${treatmentResultBlock}
          }
          ... on BodyBodyCodeBlock {
            ${codeBlockFragment}
          }
          ... on BodyBodyListing {
            __typename
            listing {
              background_color
              link_color
              listingsConnection {
                edges {
                  node {
                    ... on Testimonial {
                      title
                      description
                      author
                      imageConnection {
                        ${imageConnectionFragment}
                      }
                    }
                  }
                }
              }
              sub_head
              title
              type
            }
          }
          ... on BodyBodyPointersBlock {
            ${pointerBlockFragment}
          }
          ... on BodyBodyText {
            ${textFragment}
          }
        }
      }
    }
  }
}`

export const TreatmentTypeDetailPageQuery = (type: string) => `{
  all_dynamic_page(where: {url: "${ROUTES.TREATMENT_TYPE_DETAIL}/${type}/"}) {
    items {
      title
      url
      background_imageConnection {
        ${imageConnectionFragment}
      }
      mobile_background_imageConnection{
        ${imageConnectionFragment}
      }
      ${seoFragment}
      ${promoBannerFragement}
      content {
        body {
          ... on BodyBodyCallout {
            ${calloutBlockFragment}
          }
          ... on BodyBodyCarousel {
            ${carouselBlockFragment}
          }
          ... on BodyBodyListing {
            __typename
            listing {
              background_color
              imageConnection {
               ${imageConnectionFragment}
              }
              link_color
              sub_head
              title
              type
              layout_type
              more_link {
                href
                title
              }
              listingsConnection {
                edges {
                  node {
                    ... on Treatment {
                      title
                      duration
                      url
                      description
                      short_description
                      imageConnection {
                        ${imageConnectionFragment}
                      }
                    }
                  }
                }
              }
            }
          }
          ... on BodyBodyPointersBlock {
            ${pointerBlockFragment}
          }
          
        }
      }
    }
  }
}`
export const TrainingPageQuery = () => `{
  all_dynamic_page(where: {url: "${ROUTES.TRAINING}"}, limit: 1) {
    items {
      background_imageConnection {
        ${imageConnectionFragment}
      }
      mobile_background_imageConnection{
        ${imageConnectionFragment}
      }
      title
      url
      content {
        body {
          ... on BodyBodyCallout {
            ${calloutBlockFragment}
          }
          ... on BodyBodyListing {
            __typename
            listing {
              title
              type
              listingsConnection {
                edges {
                  node {
                    ... on Testimonial {
                      title
                      description
                      author
                      imageConnection {
                        ${imageConnectionFragment}
                      }
                    }
                  }
                }
              }
            }
          }
          ... on BodyBodyPointersBlock {
            ${pointerBlockFragment}
          }
          ... on BodyBodyImageGrid {
            ${imageGridFragment}
          }
          ... on BodyBodyCodeBlock {
            ${codeBlockFragment}
          }
          ... on BodyBodyText {
            ${textFragment}
          }
        }
      }
      ${seoFragment}
      ${promoBannerFragement}
    }
  }
}`

export const ClaimsPageQuery = () => `{
  all_dynamic_page(where: {url: "${ROUTES.CLAIMS}"}) {
    items {
      title
      url
      background_imageConnection {
        ${imageConnectionFragment}
      }
      mobile_background_imageConnection{
        ${imageConnectionFragment}
      }
      content {
        body {
          ... on BodyBodyCallout {
            ${calloutBlockFragment}
          }
          ... on BodyBodyContentGrid {
            ${contentGridFragment}
          }
          ... on BodyBodySplitImageBlock {
            ${splitImageBlockFragment}
          }
          ... on BodyBodyPointersBlock {
            ${pointerBlockFragment}
          }
          ... on BodyBodyText {
            ${textFragment}
          }
        }
      }
      ${seoFragment}
      ${promoBannerFragement}
    }
  }
}`
export const AboutPageQuery = () => `{
  all_dynamic_page(where: {url: "${ROUTES.ABOUT_US}"}, limit: 1) {
    items {
      title
      url
      ${seoFragment}
      ${promoBannerFragement}
      content {
        body {
          ... on BodyBodySplitImageBlock {
            ${splitImageBlockFragment}
          }
          ... on BodyBodyCallout {
            ${calloutBlockFragment}
          }
          ... on BodyBodyListing {
            __typename
            listing {
              type
              title
              listingsConnection {
                edges {
                  node {
                    ... on Team {
                      title
                      designation
                      bio
                      imageConnection {
                        ${imageConnectionFragment}
                      }
                    }
                  }
                }
              }
            }
          }
          ... on BodyBodyPointersBlock {
            ${pointerBlockFragment}
          }
          ... on BodyBodyText {
            ${textFragment}
          }
        }
      }
    }
  }
}`

export const CareersPageQuery = () => `{
  all_dynamic_page(where: {url: "${ROUTES.CAREERS}"}, limit: 1) {
    items {
      title
      url
      ${seoFragment}
      ${promoBannerFragement}
      content {
        body {
          ... on BodyBodySplitImageBlock {
            ${splitImageBlockFragment}
          }
          ... on BodyBodyCallout {
            ${calloutBlockFragment}
          }
          ... on BodyBodyPointersBlock {
            ${pointerBlockFragment}
          }
          ... on BodyBodyCodeBlock {
           ${codeBlockFragment}
          }
        }
      }
    }
  }
}`

export const HowItWorksPageQuery = () => `{
  all_dynamic_page(where: {url: "${ROUTES.HOW_IT_WORKS}"}, limit: 1) {
    items {
      title
      url
      background_imageConnection {
        ${imageConnectionFragment}
      }
      mobile_background_imageConnection {
        ${imageConnectionFragment}
      }
      description
      ${seoFragment}
      ${promoBannerFragement}
      content {
        body {
          ... on BodyBodySplitImageBlock {
            ${splitImageBlockFragment}
          }
          ... on BodyBodyCallout {
            ${calloutBlockFragment}
          }
          ... on BodyBodyPointersBlock {
            ${pointerBlockFragment}
          }
          ... on BodyBodyText {
            ${textFragment}
          }
          ... on BodyBodyCarousel {
            ${carouselBlockFragment}
          }
          ... on BodyBodyTreatmentResults {
            ${treatmentResultBlock}
          }
        }
      }
    }
  }
}`

export const SubCategoriesQuery = (category: any) => `{
  all_sub_category(where: {category: "${category}"}) {
    items {
      title
      category
    }
  }
}`

export const ClientHomePageQuery = () => `{
  all_dynamic_page(where: {url: "${ROUTES.HOME}home"}, limit: 1) {
    items {
      title
      url
      ${seoFragment}
      ${promoBannerFragement}
      banner_logoConnection {
        ${imageConnectionFragment}
      }
      content {
        body {
          ... on BodyBodySplitImageBlock {
            ${splitImageBlockFragment}
          }
          ... on BodyBodyContentGrid {
           ${contentGridFragment}
          }
          ... on BodyBodyPointersBlock {
            ${pointerBlockFragment}
          }
          ... on BodyBodyCarousel {
            ${carouselBlockFragment}
          }
          ... on BodyBodyImageGrid {
            ${imageGridFragment}
          }   
          ... on BodyBodyCallout {
            ${calloutBlockFragment}
          }
          ... on BodyBodyText {
            ${textFragment}
           } 
        }
      }
    }
  }
}`

export const HydralockHaBoosterPageQuery = () => {
    return `{
  all_dynamic_page(where: {url: "${ROUTES.HYDRALOCK_HA_BOOSTER}"}, limit: 1) {
    items {
      title
      url
      ${seoFragment}
      ${promoBannerFragement}
      banner_logoConnection {
        ${imageConnectionFragment}
      }
      content {
        body {
          ... on BodyBodySplitImageBlock {
            ${splitImageBlockFragment}
          }
          ... on BodyBodyPointersBlock {
            ${pointerBlockFragment}
          }
          ... on BodyBodyContentGrid {
           ${contentGridFragment}
          }          
          ... on BodyBodyPointersBlock {
            ${pointerBlockFragment}
          }
        }
      }
    }
  }
}`
}

export const HydrafacialInTheNewsPageQuery = () => {
    return `{
      all_dynamic_page(where: {url: "${ROUTES.HYDRAFACIAL_IN_THE_NEWS}"} limit:1) {
        items {
          title
          url
          is_title_visible_on_mobile
          background_imageConnection {
        ${imageConnectionFragment}
      }
      mobile_background_imageConnection{
        ${imageConnectionFragment}
      }
          ${seoFragment}
          ${promoBannerFragement}
          banner_logoConnection {
            ${imageConnectionFragment}
          }
          content {
            body {
              ... on BodyBodyListing {
                __typename
                listing {
                  background_color
                  imageConnection {
                    ${imageConnectionFragment}
                  }
                  link_color
                  sub_head
                  title
                  type
                  description
                  custom_styling
                  click_id
                  logo_imageConnection {
                    ${imageConnectionFragment}
                  }
                  more_link {
                    href
                    title
                  }
                  listingsConnection {
                    edges {
                      node {
                        ... on Treatment {
                          description
                          imageConnection {
                            ${imageConnectionFragment}
                          }
                          content {
                            body {
                              ... on BodyBodyListing {
                                listing {
                                   imageConnection {
                    ${imageConnectionFragment}
                  }
                  description
                  logo_imageConnection {
                    ${imageConnectionFragment}
                  }
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
              ... on BodyBodyImageGrid {
                ${imageGridFragment}
              }
              ... on BodyBodyText {
                  ${textFragment}
              }
            }
          }
        }
      }
    }
  `
}

export const ProviderHomePageQuery = () => `{
  all_dynamic_page(where: {url: "${ROUTES.FOR_PROFESSIONAL}"}, limit: 1) {
    items {
      title
      background_imageConnection {
        ${imageConnectionFragment}
      }
      mobile_background_imageConnection{
        ${imageConnectionFragment}
      }
      url
      ${seoFragment}
      ${promoBannerFragement}
      content {
        body {
          ... on BodyBodyCallout {
            ${calloutBlockFragment}
          }
          ... on BodyBodyCarousel {
            ${carouselBlockFragment}
          }
          ... on BodyBodyCodeBlock {
           ${codeBlockFragment}
          }
          ... on BodyBodyListing {
            __typename
            listing {
              background_color
              link_color
              listingsConnection {
                edges {
                  node {
                    ... on Testimonial {
                      title
                      description
                      author
                      imageConnection {
                        ${imageConnectionFragment}
                      }
                    }
                  }
                }
              }
              sub_head
              title
              type
            }
          }
          ... on BodyBodyPointersBlock {
            ${pointerBlockFragment}
          }
          ... on BodyBodyImageGrid {
            ${imageGridFragment}
          }
        }
      }
    }
  }
}`

export const CustomerProviderPageQuery = () => `{
  all_dynamic_page(where: {url: "${ROUTES.CUSTOMER_PROVIDER}"}, limit: 1) {
    items {
      title
      background_imageConnection {
        ${imageConnectionFragment}
      }
      mobile_background_imageConnection{
        ${imageConnectionFragment}
      }
      url
      ${seoFragment}
      ${promoBannerFragement}
      content {
        body {
          ... on BodyBodyCallout {
            ${calloutBlockFragment}
          }
          ... on BodyBodySplitImageBlock {
            ${splitImageBlockFragment}
          }
          ... on BodyBodyCarousel {
            ${carouselBlockFragment}
          }
         
        }
      }
    }
  }
}`

export const TestimonialProviderPageQuery = () => `{
  all_dynamic_page(where: {url: "${ROUTES.PROFESSIONAL_TESTIMONIALS}"}, limit: 1) {
    items {
      title
      background_imageConnection {
        ${imageConnectionFragment}
      }
      mobile_background_imageConnection{
        ${imageConnectionFragment}
      }
      url
      ${seoFragment}
      ${promoBannerFragement}
      content {
        body {
          ... on BodyBodyCallout {
            ${calloutBlockFragment}
          }
          ... on BodyBodySplitImageBlock {
            ${splitImageBlockFragment}
          }
          ... on BodyBodyCarousel {
            ${carouselBlockFragment}
          }
          ... on BodyBodyCodeBlock {
            ${codeBlockFragment}
          }
          ... on BodyBodyImageGrid {
            ${imageGridFragment}
          }
          ... on BodyBodyPointersBlock {
            ${pointerBlockFragment}
          }
        }
      }
    }
  }
}`

export const SubmissionPageQuery = () => `{
  all_dynamic_page(where: {url: "${ROUTES.EMAIL_SUBMISSION}"}, limit: 1) {
    items {
      title
      background_imageConnection {
        ${imageConnectionFragment}
      }
      mobile_background_imageConnection{
        ${imageConnectionFragment}
      }
      url
      ${seoFragment}
      ${promoBannerFragement}
      content {
        body {
          ... on BodyBodyText {
            ${textFragment}
           }
        }
      }
    }
  }
}`

export const PrivacyPolicePageQuery = () => `{
  all_dynamic_page(where: {url: "${ROUTES.PRIVACY}"}) {
    items {
      title
      ${seoFragment}
      ${promoBannerFragement}
      content {
        body {
          ... on BodyBodyCallout {
            ${calloutBlockFragment}
          }
          ... on BodyBodyText {
           ${textFragment}
          }
        }
      }
    }
  }
}`

export const BoostersPageQuery = (
    type?: string,
    url?: string,
    path?: string
) => `{
  all_dynamic_page(where: {url: "${
      path === 'consumer' ? ROUTES.CONSUMER_BOOSTER : ROUTES.BOOSTERS
  }"}) {
    items {
      background_imageConnection {
        ${imageConnectionFragment}
      }
      mobile_background_imageConnection{
        ${imageConnectionFragment}
      }
      title
      description
      ${seoFragment}
      ${promoBannerFragement}
      content {
        body {
          ... on BodyBodyListing {
            __typename
            listing {
              type
              title
              listingsConnection {
                edges {
                  node {
                    ... on BoosterCategory {
                      ${path === 'consumer' ? seoFragment : providerSeoFragment}
                      display_title
                      type
                      url
                      title
                    }
                  }
                }
              }
            }
          }
          ... on BodyBodyCallout {
            ${calloutBlockFragment}
           }
        }
      }
    }
  }
  
  all_boosters(where: {category: {booster_category: ${
      type && url && `{url: "/${url}/", type: "${type}"}`
  }}}) {
    items {
      title
      sub_head
      imageConnection {
        ${imageConnectionFragment}
      }
      benefits {
        title
        note
      }
      ingredients {
        title
      }
      b2b_only
      category_order
    }
  }
}`
export const FreshAccessPageQuery = () => `{
  all_dynamic_page(where:{url: "${ROUTES.FRESH_ACCESS}"})
 {
    items {
      title
      ${seoFragment}
      ${promoBannerFragement}
      content {
        body {
          ... on BodyBodyCallout {
            __typename
            callout {
              title
              description
            }
          }
          ... on BodyBodySplitImageBlock {
            ${splitImageBlockFragment}
          }
        }
      }
    }
  }
}`

export const HkxPageQuery = () => `{
  all_dynamic_page(where:{url: "${ROUTES.HKX}"}) {
    items {
      background_imageConnection {
        ${imageConnectionFragment}
      }
      mobile_background_imageConnection{
        ${imageConnectionFragment}
      }
      title
      ${seoFragment}
      ${promoBannerFragement}
      content {
        body {
          ... on BodyBodyCallout {
            ${calloutBlockFragment}
           }
          ... on BodyBodySplitImageBlock {
            ${splitImageBlockFragment}
          }
          ... on BodyBodyText {
            ${textFragment}          }
        }
      }
    }
  }
}
`
export const PatentsPageQuery = () => `{
  all_dynamic_page(where: {url: "${ROUTES.PATENTS}"}) {
    items {
      title
      ${seoFragment}
      ${promoBannerFragement}
      content {
        body {
          ... on BodyBodyCallout {
            ${calloutBlockFragment}
          }
          ... on BodyBodySplitImageBlock {
            ${splitImageBlockFragment}
          }
          ... on BodyBodyPointersBlock {
            ${pointerBlockFragment}
          }
          ... on BodyBodySplitTextBlock {
            ${splitTextBlockFragment}
          }
        }
      }
    }
  }
}`

export const HfxPageQuery = () => `{
  all_dynamic_page(where:{url: "${ROUTES.HFX}"}) {
    items {
      background_imageConnection {
        ${imageConnectionFragment}
      }
      mobile_background_imageConnection{
        ${imageConnectionFragment}
      }
      title
      ${seoFragment}
      ${promoBannerFragement}
      content {
        body {
          ... on BodyBodyCallout {
            ${calloutBlockFragment}
           }
          ... on BodyBodySplitImageBlock {
            ${splitImageBlockFragment}
          }
          ... on BodyBodyText {
            ${textFragment}          }
        }
      }
    }
  }
}
`
export const IfuPageQuery = () => `{
  all_dynamic_page(where: {url: "${ROUTES.IFU}"}) {
    items {
      url
      title
      description
      ${seoFragment}
      ${promoBannerFragement}
      content {
        body {
          ... on BodyBodyCallout {
            ${calloutBlockFragment}
          }
          ... on BodyBodyListing {
            __typename
            listing {
              background_color
              description
              layout_type
              link_color
              sub_head
              title
              type
              listingsConnection {
                edges {
                  node {
                    ... on Manual {
                      title
                      description
                      guide {
                        title
                        versions {
                          date
                          documentConnection {
                            ${imageConnectionFragment}
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
          ... on BodyBodyText {
            ${textFragment}
          }
        }
      }
    }
  }
}`

export const JloClientHomePageQuery = () => `{
  all_dynamic_page(where: {url: "${ROUTES.JLO_CLIENT_HOME}"}, , limit: 1) {
    total
    items {
      title
      url
      banner_description
      background_imageConnection {
        ${imageConnectionFragment}
      }
      mobile_background_imageConnection {
        ${imageConnectionFragment}
      }
      banner_logoConnection {
        ${imageConnectionFragment}
      }
      description
      ${seoFragment}
      ${promoBannerFragement}
      content {
        body {
          ... on BodyBodyCallout {
            ${calloutBlockFragment}
          }
          ... on BodyBodyPointersBlock {
            ${pointerBlockFragment}
          }
          ... on BodyBodySplitImageBlock {
            ${splitImageBlockFragment}
          }
          ... on BodyBodyCodeBlock {
            ${codeBlockFragment}
          }
          ... on BodyBodyListing {
            __typename
            listing {
              background_color
              description
              imageConnection {
                edges {
                  node {
                    url
                    title
                  }
                }
              }
              layout_type
              type
              link_color
              listingsConnection {
                edges {
                  node {
                    ... on Testimonial {
                      title
                      description
                      author
                      background_imageConnection {
                        edges {
                          node {
                            title
                            url
                          }
                        }
                      }
                      imageConnection {
                        edges {
                          node {
                            title
                            url
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}`

export const JloProviderHomePageQuery = () => `{
  all_dynamic_page(where: {url: "${ROUTES.JLOPROVIDERHOME}"},limit: 1) {
    items {
      url
      banner_description
      background_imageConnection {
        ${imageConnectionFragment}
      }
      banner_logoConnection {
        ${imageConnectionFragment}
      }
      mobile_background_imageConnection{
        ${imageConnectionFragment}
      }
      content {
        body {
          ... on BodyBodyCodeBlock {
            ${codeBlockFragment}
          }
          ... on BodyBodyCallout {
           ${calloutBlockFragment}
          }
          ... on BodyBodySplitImageBlock {
            ${splitImageBlockFragment}
          }
          ... on BodyBodyListing {
            __typename
            listing {
              listingsConnection {
                edges {
                  node {
                    ... on Testimonial {
                      ${testimonialFragment}
                    }
                  }
                }
              }
            }
          }
        }
      }
      ${seoFragment}
      ${promoBannerFragement}
    }
  }
}`
export const TermsPageQuery = () => `{
  all_dynamic_page(where: {url: "${ROUTES.TERMS}"}) {
    items {
      title
      ${seoFragment}
      ${promoBannerFragement}
      content {
        body {
            ... on BodyBodyText {
           ${textFragment}
          }
        }
      }
    }
  }
}`
export const SupplierPageQuery = () => `{
  all_dynamic_page(where: {url: "${ROUTES.SUPPLIER}"}) {
    items {
      title
      ${seoFragment}
      ${promoBannerFragement}
      content {
        body {
          ... on BodyBodyText {
           ${textFragment}
          }
        }
      }
    }
  }
}`

export const EventPageQuery = () => `{
  all_dynamic_page(where: {url: "${ROUTES.EVENTS}"}) {
    items {
      title
      background_imageConnection {
        ${imageConnectionFragment}
      }
      mobile_background_imageConnection{
        ${imageConnectionFragment}
      }
      ${seoFragment}
      ${promoBannerFragement}
    }
  }
}`

export const eventQuery = (
    type: string,
    isoDateString: string,
    page: number
) => `{
    all_event(where: {${
        type === 'past' ? 'start_date_lte' : 'start_date_gte'
    }: "${isoDateString}"}, limit:5,skip:${page}) {
      total
      items {
        title
        start_date
        location
        end_date
        start_date_timestamp
        link {
          href
          title
        }
        imageConnection {
          edges {
            node {
              title
              url
            } 
          }
        }
      }
    }
}`

export const KeravivePageQuery = () => `{
  all_dynamic_page(where: {url: "${ROUTES.KERAVIVE}"}, limit: 1) {
    total
    items {
      title
      url
      description
      ${seoFragment}
      ${promoBannerFragement}
      content {
        body {
          ... on BodyBodySplitImageBlock {
            ${splitImageBlockFragment}
          }
          ... on BodyBodyListing {
            __typename
            listing {
              type
              title
              description
              listingsConnection {
                edges {
                  node {
                    ... on Testimonial {
                      title
                      sub_title
                      description
                      author
                      imageConnection {
                        ${imageConnectionFragment}
                      }
                    }
                  }
                }
              }
              background_color
            }
          }
          ... on BodyBodyPointersBlock {
            ${pointerBlockFragment}
          }
          ... on BodyBodyTreatmentResults {
            ${treatmentResultBlock}
          }
          ... on BodyBodyCallout {
            ${calloutBlockFragment}
          }
        }
      }
    }
  }
  }`

export const KeraviveB2BPageQuery = () => `{
    all_dynamic_page(where: {url: "${ROUTES.KERAVIVE_B2B}"}, limit: 1) {
      items {
        title
        background_imageConnection {
          ${imageConnectionFragment}
        }
        mobile_background_imageConnection{
          ${imageConnectionFragment}
        }
        url
        ${seoFragment}
        ${promoBannerFragement}
        content {
          body {
            ... on BodyBodyCallout {
              ${calloutBlockFragment}
            }
            ... on BodyBodySplitImageBlock {
              ${splitImageBlockFragment}
            }
            ... on BodyBodyListing {
              __typename
              listing {
                background_color
                link_color
                listingsConnection {
                  edges {
                    node {
                      ... on Testimonial {
                        title
                        description
                        author
                        imageConnection {
                          ${imageConnectionFragment}
                        }
                      }
                    }
                  }
                }
                sub_head
                title
                type
              }
            }
            ... on BodyBodyPointersBlock {
              ${pointerBlockFragment}
            }
            ... on BodyBodyImageGrid {
              ${imageGridFragment}
            }
          }
        }
      }
    }
  }`

export const FindHydrafacialistPageQuery = () => `{
  all_dynamic_page(where: {url: "${ROUTES.FIND_HYDRAFACIALIST}"}) {
    items {
      title
      url
      ${seoFragment}
      ${promoBannerFragement}
    }
  }
  }`

export const PostDetailsPageQuery = () => `{
  all_dynamic_page(where: {url: "${ROUTES.POST_DETAILS}"}) {
    items {
      title
      url
      ${promoBannerFragement}
    }
  }
  }`

export const SearchPageQuery = () => `{
  all_dynamic_page(where: {url: "${ROUTES.SEARCH}"}) {
    items {
      title
      url
      ${promoBannerFragement}
    }
  }
  }`

export const HfxclusivePageQuery = () => `{
    all_dynamic_page(where:{url: "${ROUTES.HFXCLUSIVE}"}) {
      items {
        background_imageConnection {
          ${imageConnectionFragment}
        }
        mobile_background_imageConnection{
          ${imageConnectionFragment}
        }
        title
        ${seoFragment}
        ${promoBannerFragement}
        content {
          body {
            ... on BodyBodySplitImageBlock {
              ${splitImageBlockFragment}
            }
            ... on BodyBodyText {
              ${textFragment}          
            }
          }
        }
      }
    }
  }
  `
export const glowTogetherPageQuery = () => `{
    all_dynamic_page(where:{url: "${ROUTES.GLOWTOGETHER}"}) {
      items {
        background_imageConnection {
          ${imageConnectionFragment}
        }
        mobile_background_imageConnection{
          ${imageConnectionFragment}
        }
        title
        ${seoFragment}
        ${promoBannerFragement}
        content {
          body {
            ... on BodyBodyText {
              ${textFragment}          
            }
          }
        }
      }
    }
  }
  `
export const ExploreHydrafacialPageQuery = () => `{
    all_dynamic_page(where:{url: "${ROUTES.EXPLORE_HYDRAFACIAL}"}) {
      items {
        background_imageConnection {
          ${imageConnectionFragment}
        }
        mobile_background_imageConnection{
          ${imageConnectionFragment}
        }
        title
        ${seoFragment}
        ${promoBannerFragement}
        content {
          body {
            ... on BodyBodyContentGrid {
              ${contentGridFragment}
             }
          }
        }
      }
    }
  }
  `

export const NavigationQuery = (title: string) => `{
    all_navigation(where: {title: "${title}"}) {
      items {
        group_navigation {
          group {
            click_id
            link {
              href
              title
            }
          }
          title
          click_id
        }
        main_navigation {
          click_id
          link {
            href
            title
          }
          type
        }
        navigation {
          click_id
          link {
            href
            title
          }
          type
        }
        title
      }
    }
  }`

export const KeraviveB2CPageQuery = () => `{
    all_dynamic_page(where: {url: "${ROUTES.KERAVIVE_B2C}"}, limit: 1) {
      items {
        background_imageConnection {
          ${imageConnectionFragment}
        }
        mobile_background_imageConnection{
          ${imageConnectionFragment}
        }
        url
        ${seoFragment}
        ${promoBannerFragement}
        content {
          body {
            ... on BodyBodyCarousel {
              ${carouselBlockFragment}
            }
            ... on BodyBodyCallout {
              ${calloutBlockFragment}
            }
            ... on BodyBodyText {
              ${textFragment}          
            }
            ... on BodyBodyTreatmentResults {
              ${treatmentResultBlock}
            }
            ... on BodyBodyPointersBlock {
              ${pointerBlockFragment}
            }
          }
        }
      }
    }
  }`

export const LoyaltyAppQuery = () => `{
  web_app_config(uid: "blt4bf8d94555f0e1d7") {
    itemsConnection {
      edges {
        node {
          ... on AppConfigField {
            title
            value
          }
        }
      }
    }
  }
  }`

export const SchoolsPageQuery = () => `{
    all_dynamic_page(where: {url: "${ROUTES.SCHOOL}"}, limit: 1) {
      items {
        background_imageConnection {
          ${imageConnectionFragment}
        }
        mobile_background_imageConnection{
          ${imageConnectionFragment}
        }
        url
        ${seoFragment}
        ${promoBannerFragement}
        description
        title
        content {
          body {
            ... on BodyBodyListing {
              __typename
              listing {
                description
                layout_type
                title
                type
                listingsConnection {
                  edges {
                    node {
                      ... on ProductCategory {
                        title
                        description
                        imageConnection {
                          ${imageConnectionFragment}
                        }
                      }
                    }
                  }
                }
              }
            }
           ... on BodyBodyQuote {
              __typename
              quote {
                author
                quote_description
                title
                about_author {
                  address
                  designation
                  name
                }
                avatar_imageConnection {
                  edges {
                    node {
                      url
                    }
                  }
                }
              }
            }
            ... on BodyBodyPointersBlock {
              ${pointerBlockFragment}
              }
          }
        }
      }
    }
  }`

export const LoyaltyLandingPageQuery = () => `{
    all_dynamic_page(where: {url: "${ROUTES.LOYALTY_LANDING}"}, limit: 1) {
      items {
        background_imageConnection {
          ${imageConnectionFragment}
        }
        banner_logoConnection {
          ${imageConnectionFragment}
        }
        mobile_background_imageConnection{
          ${imageConnectionFragment}
        }
        url
        ${seoFragment}
        ${promoBannerFragement}
        description
        title
        content {
          body {
            ... on BodyBodyListing {
              __typename
              listing {
                description
                layout_type
                title
                type
                listingsConnection {
                  edges {
                    node {
                      ... on ProductCategory {
                        title
                        description
                        imageConnection {
                          ${imageConnectionFragment}
                        }
                      }
                    }
                  }
                }
              }
            }
            ... on BodyBodySplitImageBlock {
              ${splitImageBlockFragment}
            }
            ... on BodyBodyCallout {
              ${calloutBlockFragment}
             }
             ... on BodyBodyImageGrid {
              ${imageGridFragment}
            }
          }
        }
      }
    }
  }`

export const DashboardPageQuery = () => `{
    all_dynamic_page(where: {url: "${ROUTES.DASHBOARD_PAGE}"}, limit: 1) {
      items {
        content {
          body {
            ... on BodyBodyDashboardPromoBanner {
              __typename
              dashboard_promo_banner {
                description
                enabled
                click_id
                footnote
                title
                sub_title
                mobile_background_imageConnection {
                  edges {
                    node {
                      url
                    }
                  }
                }
                link {
                  title
                  href
                }
                background_imageConnection {
                  edges {
                    node {
                      url
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }`
